import { safeTimeZone } from "./timezone";
import { isOpenOn } from "./opening-hours";
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MINUTE_MS = 60000;
const HOUR_MS = 60 * MINUTE_MS;
const CLOCK_SKEW_GRACE_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
export function minutesOfDay(hhmm) {
    const [h, m] = hhmm.split(":");
    const hours = Number(h);
    const mins = Number(m);
    if (!Number.isFinite(hours) || !Number.isFinite(mins))
        return 0;
    return hours * 60 + mins;
}
function zoneOffsetMs(tz, instant) {
    const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    const p = Object.fromEntries(fmt.formatToParts(instant).map((x) => [x.type, x.value]));
    const hour = p.hour === "24" ? 0 : Number(p.hour);
    const asIfUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second));
    return asIfUtc - instant.getTime();
}
export function zonedTimeToUtc(tz, ymd, minutes) {
    const [y, m, d] = ymd.split("-").map(Number);
    const naive = Date.UTC(y, (m !== null && m !== void 0 ? m : 1) - 1, d !== null && d !== void 0 ? d : 1, 0, 0, 0) + minutes * MINUTE_MS;
    const first = zoneOffsetMs(tz, new Date(naive));
    let ts = naive - first;
    const second = zoneOffsetMs(tz, new Date(ts));
    if (second !== first)
        ts = naive - second;
    return new Date(ts);
}
export function localDayInZone(tz, instant) {
    var _a;
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
    });
    const p = Object.fromEntries(fmt.formatToParts(instant).map((x) => [x.type, x.value]));
    const map = {
        Sun: "sun",
        Mon: "mon",
        Tue: "tue",
        Wed: "wed",
        Thu: "thu",
        Fri: "fri",
        Sat: "sat",
    };
    return {
        ymd: `${p.year}-${p.month}-${p.day}`,
        day: (_a = map[p.weekday]) !== null && _a !== void 0 ? _a : "mon",
    };
}
function subtract(spans, cuts) {
    let out = spans;
    for (const cut of cuts) {
        const next = [];
        for (const s of out) {
            if (cut.end <= s.start || cut.start >= s.end) {
                next.push(s);
                continue;
            }
            if (cut.start > s.start)
                next.push({ start: s.start, end: cut.start });
            if (cut.end < s.end)
                next.push({ start: cut.end, end: s.end });
        }
        out = next;
    }
    return out;
}
function layerByPrecedence(override, plan, fallback) {
    const cut = (spans, cuts) => spans.flatMap((s) => subtract([{ start: s.start, end: s.end }], cuts).map((piece) => ({
        ...s,
        ...piece,
    })));
    const planCut = cut(plan, override);
    const fallbackCut = cut(fallback, [...override, ...planCut]);
    return [...override, ...planCut, ...fallbackCut].sort((a, b) => a.start - b.start);
}
export function campaignCovers(campaign, ymd, day, minutes) {
    if (!campaign.enabled)
        return false;
    if (campaign.items.length === 0)
        return false;
    if (campaign.startDate && ymd < campaign.startDate)
        return false;
    if (campaign.endDate && ymd > campaign.endDate)
        return false;
    if (campaign.days.length > 0 && !campaign.days.includes(day))
        return false;
    const start = minutesOfDay(campaign.startTime);
    const end = minutesOfDay(campaign.endTime);
    if (start === end)
        return true;
    if (start < end)
        return minutes >= start && minutes < end;
    return minutes >= start || minutes < end;
}
function rotationRegime(distinct, highs, perHour, perBreak) {
    const turnsPerHour = perHour * perBreak;
    if (turnsPerHour >= distinct + highs)
        return "continuous";
    if (turnsPerHour >= distinct)
        return "restart";
    return "scarce";
}
function rotationOrder(live, regime, hourIndex) {
    const highs = live.filter((c) => c.priority === "high");
    const distinct = [...highs, ...live.filter((c) => c.priority !== "high")];
    if (regime === "scarce" || highs.length === 0)
        return distinct;
    if (regime === "restart") {
        const shift = ((hourIndex % highs.length) + highs.length) % highs.length;
        return [
            ...distinct,
            ...highs.map((_, j) => highs[(j + shift) % highs.length]),
        ];
    }
    const length = distinct.length + highs.length;
    const half = Math.ceil(length / 2);
    const slots = new Array(length);
    highs.forEach((c, j) => {
        slots[j] = c;
        slots[half + j] = c;
    });
    const normals = distinct.slice(highs.length);
    let r = 0;
    for (let i = 0; i < length; i += 1) {
        if (!slots[i])
            slots[i] = normals[r++];
    }
    return slots;
}
export const CHANNEL_PLAYLIST_PREFIX = "channel:";
export function channelPlaylistId(contentAssetId) {
    return `${CHANNEL_PLAYLIST_PREFIX}${contentAssetId}`;
}
export function channelPosterUrl(playlist) {
    if (playlist.posterUrl)
        return playlist.posterUrl;
    const first = playlist.items[0];
    if ((first === null || first === void 0 ? void 0 : first.kind) === "image" && first.assetUrl)
        return first.assetUrl;
    return null;
}
export function isChannelPlaylistId(id) {
    return id.startsWith(CHANNEL_PLAYLIST_PREFIX);
}
export function playlistIdOfBlock(block) {
    if (block.playlistId)
        return block.playlistId;
    if (block.contentAssetId)
        return channelPlaylistId(block.contentAssetId);
    return null;
}
export function channelBlockPlaylist(block, updatedAt) {
    var _a;
    const assetId = block.contentAssetId;
    return {
        id: channelPlaylistId(assetId),
        name: ((_a = block.channelName) === null || _a === void 0 ? void 0 : _a.trim()) || "Live channel",
        scope: "global",
        premiseId: null,
        companyId: null,
        items: [
            {
                id: `${CHANNEL_PLAYLIST_PREFIX}${assetId}`,
                assetUrl: "",
                contentAssetId: assetId,
                kind: "video",
                durationSeconds: 0,
                fit: "contain",
                muted: false,
            },
        ],
        defaultItemSeconds: 3600,
        soundEnabled: true,
        updatedAt,
    };
}
export function resolveSignageTimeline(input) {
    const { assignment, timezone, now, horizonHours } = input;
    const { timeZone: tz, invalid: badZone } = safeTimeZone(timezone);
    const warnings = [];
    if (badZone) {
        warnings.push({
            code: "invalid_timezone",
            value: badZone,
            message: `"${badZone}" is not a timezone this system recognises, so times were resolved in UTC. Set the location's timezone to an IANA name such as "Europe/Stockholm".`,
        });
    }
    const from = now.getTime();
    const to = from + horizonHours * HOUR_MS;
    const playlistById = new Map(input.playlists.map((p) => [p.id, p]));
    for (const dayPlan of input.dayPlans) {
        for (const block of dayPlan.blocks) {
            const contentAssetId = block.contentAssetId;
            if (!contentAssetId)
                continue;
            const id = channelPlaylistId(contentAssetId);
            if (!playlistById.has(id)) {
                playlistById.set(id, channelBlockPlaylist({ ...block, contentAssetId }, dayPlan.updatedAt));
            }
        }
    }
    const dayPlanById = new Map(input.dayPlans.map((p) => [p.id, p]));
    const override = [];
    if (assignment.override && playlistById.has(assignment.override.playlistId)) {
        const since = Date.parse(assignment.override.since);
        const until = assignment.override.until
            ? Date.parse(assignment.override.until)
            : to;
        const start = Math.max(from, since);
        const end = Math.min(to, until);
        if (end > start) {
            override.push({
                start,
                end,
                source: "override",
                playlistId: assignment.override.playlistId,
                loopStart: since,
            });
        }
    }
    const plan = [];
    const firstDay = localDayInZone(tz, new Date(from - DAY_MS));
    let cursor = zonedTimeToUtc(tz, firstDay.ymd, 0).getTime();
    const guard = to + 2 * DAY_MS;
    const seen = new Set();
    while (cursor < guard) {
        const local = localDayInZone(tz, new Date(cursor));
        if (seen.has(local.ymd)) {
            cursor += DAY_MS;
            continue;
        }
        seen.add(local.ymd);
        const dayPlanId = assignment.weekly[local.day];
        const dayPlan = dayPlanId ? dayPlanById.get(dayPlanId) : undefined;
        if (dayPlan) {
            for (const block of dayPlan.blocks) {
                const blockPlaylistId = playlistIdOfBlock(block);
                if (!blockPlaylistId || !playlistById.has(blockPlaylistId))
                    continue;
                const startMin = minutesOfDay(block.start);
                const endMin = minutesOfDay(block.end);
                if (endMin <= startMin)
                    continue;
                const s = zonedTimeToUtc(tz, local.ymd, startMin).getTime();
                const e = zonedTimeToUtc(tz, local.ymd, endMin).getTime();
                const clippedStart = Math.max(from, s);
                const clippedEnd = Math.min(to, e);
                if (clippedEnd > clippedStart) {
                    plan.push({
                        start: clippedStart,
                        end: clippedEnd,
                        source: "plan",
                        playlistId: blockPlaylistId,
                        loopStart: s,
                    });
                }
            }
        }
        cursor += DAY_MS;
    }
    const fallback = [];
    if (assignment.defaultPlaylistId &&
        playlistById.has(assignment.defaultPlaylistId)) {
        fallback.push({
            start: from,
            end: to,
            source: "default",
            playlistId: assignment.defaultPlaylistId,
            loopStart: zonedTimeToUtc(tz, localDayInZone(tz, new Date(from)).ymd, 0).getTime(),
        });
    }
    const merged = layerByPrecedence(override, plan, fallback);
    const asIntervals = (spans) => spans.map((s) => ({
        startsAt: new Date(s.start).toISOString(),
        endsAt: new Date(s.end).toISOString(),
        loopStartsAt: new Date(s.loopStart).toISOString(),
        source: s.source,
        playlistId: s.playlistId,
    }));
    const content = asIntervals(merged);
    const scheduledContent = override.length
        ? asIntervals(layerByPrecedence([], plan, fallback))
        : undefined;
    const breaks = resolveBreaks(input, tz, from, to);
    const usedIds = new Set(content.map((c) => c.playlistId));
    const playlists = [...usedIds]
        .map((id) => playlistById.get(id))
        .filter((p) => Boolean(p))
        .map((playlist) => materialiseContent(playlist, input.contentUrls, warnings));
    const adSlots = resolveAdSlots(input, tz, from, to, {
        content,
        breaks,
        playlists,
    });
    const templates = collectTemplates([...playlists.flatMap((p) => p.items), ...breaks.flatMap((b) => b.items)], input.templates, warnings);
    return {
        screenId: assignment.screenId,
        generatedAt: new Date(from).toISOString(),
        horizonEndsAt: new Date(to).toISOString(),
        timezone: tz,
        content,
        ...(scheduledContent ? { scheduledContent } : {}),
        breaks,
        ...(adSlots.length ? { adSlots } : {}),
        playlists,
        ...(templates.length ? { templates } : {}),
        soundEnabled: assignment.soundEnabled,
        revision: revisionOf(content, breaks, playlists, assignment, templates, adSlots),
        ...(warnings.length ? { warnings } : {}),
    };
}
export function breakMarkerMinutes(perHour) {
    const n = Math.floor(perHour);
    if (n <= 0)
        return [];
    const markers = [];
    for (let m = 0; m < 60; m += AD_SLOT_SPACING_MINUTES)
        markers.push(m);
    if (n >= markers.length)
        return markers;
    if (markers.length % n === 0) {
        const every = markers.length / n;
        return markers.filter((_, i) => i % every === 0);
    }
    const spacing = Math.floor(60 / n);
    return Array.from({ length: n }, (_, i) => i * spacing);
}
function resolveBreaks(input, tz, from, to) {
    const { assignment } = input;
    const perHour = Math.floor(assignment.breaksPerHour);
    if (perHour <= 0)
        return [];
    if (assignment.campaignIds.length === 0)
        return [];
    const seconds = assignment.breakSeconds;
    const eligible = input.campaigns.filter((c) => assignment.campaignIds.includes(c.id) &&
        c.enabled &&
        campaignSeconds(c) > 0 &&
        campaignSeconds(c) <= seconds);
    if (eligible.length === 0)
        return [];
    const markers = breakMarkerMinutes(perHour);
    const out = [];
    const fromLocalMinutes = localMinutesOf(tz, new Date(from));
    const dayStart = from - fromLocalMinutes * MINUTE_MS - (from % MINUTE_MS);
    const firstHour = Math.floor(dayStart / HOUR_MS) * HOUR_MS;
    let cursor = 0;
    let day = "";
    let restartedHour = Number.NaN;
    for (let hour = firstHour; hour < to; hour += HOUR_MS) {
        const hourLocal = localDayInZone(tz, new Date(hour));
        const hourMinutes = localMinutesOf(tz, new Date(hour));
        const hourIndex = Math.round(hour / HOUR_MS);
        for (const offset of markers) {
            const at = hour + Math.round(offset * MINUTE_MS);
            if (at >= to)
                break;
            let local = hourLocal;
            let localMinutes = hourMinutes + Math.floor(offset);
            if (localMinutes >= 24 * 60) {
                local = localDayInZone(tz, new Date(at));
                localMinutes = localMinutesOf(tz, new Date(at));
            }
            if (local.ymd !== day) {
                day = local.ymd;
                cursor = 0;
            }
            const live = eligible.filter((c) => campaignCovers(c, local.ymd, local.day, localMinutes));
            if (live.length === 0)
                continue;
            const highs = live.filter((c) => c.priority === "high").length;
            const regime = rotationRegime(live.length, highs, markers.length, turnsPerBreak(live, seconds));
            if (regime === "restart" && restartedHour !== hour) {
                restartedHour = hour;
                cursor = 0;
            }
            const order = rotationOrder(live, regime, hourIndex);
            const picked = fillBreak(order, cursor, seconds);
            cursor = picked.next;
            if (at + seconds * 1000 <= from || picked.items.length === 0)
                continue;
            out.push({
                startsAt: new Date(at).toISOString(),
                durationSeconds: seconds,
                items: picked.items,
                campaignIds: picked.campaignIds,
            });
        }
    }
    return out;
}
export const AD_SLOT_SPACING_MINUTES = 6;
export const AD_SLOT_MAX_SECONDS = 60;
function resolveAdSlots(input, tz, from, to, resolved) {
    var _a, _b, _c, _d, _e, _f;
    const venueId = (_b = (_a = input.ads) === null || _a === void 0 ? void 0 : _a.vistarVenueId) === null || _b === void 0 ? void 0 : _b.trim();
    const hours = (_d = (_c = input.ads) === null || _c === void 0 ? void 0 : _c.openingHours) !== null && _d !== void 0 ? _d : undefined;
    if (!venueId || !hours)
        return [];
    const probe = {
        screenId: input.assignment.screenId,
        generatedAt: new Date(from).toISOString(),
        horizonEndsAt: new Date(to).toISOString(),
        timezone: tz,
        content: resolved.content,
        breaks: resolved.breaks,
        playlists: resolved.playlists,
        soundEnabled: input.assignment.soundEnabled,
        revision: "",
    };
    const slotMs = AD_SLOT_MAX_SECONDS * 1000;
    const out = [];
    const firstHour = Math.floor((from - slotMs) / HOUR_MS) * HOUR_MS;
    for (let hour = firstHour; hour < to; hour += HOUR_MS) {
        const hourMinutes = localMinutesOf(tz, new Date(hour));
        for (let m = 0; m < 60; m += 1) {
            if ((hourMinutes + m) % AD_SLOT_SPACING_MINUTES !== 0)
                continue;
            const at = hour + m * MINUTE_MS;
            if (at >= to)
                break;
            if (at + slotMs <= from)
                continue;
            const instant = new Date(at);
            const local = localDayInZone(tz, instant);
            if (!isOpenOn(hours, local.day, localMinutesOf(tz, instant)))
                continue;
            if (!breakMayInterrupt(probe, instant))
                continue;
            const head = playheadAt(probe, instant);
            if (((_f = (_e = head === null || head === void 0 ? void 0 : head.playlist.adPermissions) === null || _e === void 0 ? void 0 : _e.external) === null || _f === void 0 ? void 0 : _f.fullTakeoverAllowed) === false)
                continue;
            out.push({
                startsAt: instant.toISOString(),
                maxSeconds: AD_SLOT_MAX_SECONDS,
            });
        }
    }
    return out;
}
function turnsPerBreak(live, seconds) {
    const total = live.reduce((sum, c) => sum + campaignSeconds(c), 0);
    const average = total / live.length;
    return Math.max(1, Math.floor(seconds / average));
}
function collectTemplates(items, resolved, warnings) {
    const wanted = [
        ...new Set(items
            .map((i) => i.templateInstanceId)
            .filter((id) => typeof id === "string" && id !== "")),
    ];
    if (wanted.length === 0)
        return [];
    const out = [];
    for (const id of wanted) {
        const hit = resolved === null || resolved === void 0 ? void 0 : resolved[id];
        if (hit) {
            out.push(hit);
            continue;
        }
        warnings.push({
            code: "template_unavailable",
            value: id,
            message: "A slide in this schedule no longer exists, so it shows nothing. Remove it from the playlist, or rebuild it from its template.",
        });
    }
    return out;
}
function materialiseContent(playlist, contentUrls, warnings) {
    var _a;
    if (!playlist.items.some((item) => item.contentAssetId))
        return playlist;
    const items = [];
    let channelLogoUrl = null;
    for (const item of playlist.items) {
        if (!item.contentAssetId) {
            items.push(item);
            continue;
        }
        const source = (_a = contentUrls === null || contentUrls === void 0 ? void 0 : contentUrls[item.contentAssetId]) !== null && _a !== void 0 ? _a : null;
        if (!source) {
            warnings.push({
                code: "content_unavailable",
                value: item.contentAssetId,
                message: `A catalogue title in "${playlist.name}" has no playable file right now, so it was left out of the loop. It may have been withdrawn, or re-packaged into something with no rendition this player can license.`,
            });
            continue;
        }
        if (source.live && source.logoUrl && !channelLogoUrl) {
            channelLogoUrl = source.logoUrl;
        }
        items.push({
            ...item,
            assetUrl: source.url,
            live: source.live,
            protocol: source.protocol,
            encrypted: source.encrypted,
            ...(!source.live && source.logoUrl
                ? { supplierLogoUrl: source.logoUrl }
                : {}),
        });
    }
    return {
        ...playlist,
        items,
        ...(channelLogoUrl && !playlist.logoUrl
            ? { effect: true, logoUrl: channelLogoUrl }
            : {}),
    };
}
export function localMinutesOf(tz, instant) {
    const fmt = new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
    });
    const p = Object.fromEntries(fmt.formatToParts(instant).map((x) => [x.type, x.value]));
    const hour = p.hour === "24" ? 0 : Number(p.hour);
    return hour * 60 + Number(p.minute);
}
function fillBreak(order, cursor, seconds) {
    const items = [];
    const campaignIds = [];
    if (order.length === 0)
        return { items, campaignIds, next: cursor };
    let used = 0;
    let firstSkipped;
    let pos = cursor;
    for (let scanned = 0; scanned < order.length && used < seconds; scanned += 1) {
        const at = pos;
        const c = order[((at % order.length) + order.length) % order.length];
        pos += 1;
        if (campaignIds.includes(c.id))
            continue;
        const cost = campaignSeconds(c);
        if (used + cost > seconds) {
            if (firstSkipped === undefined)
                firstSkipped = at;
            continue;
        }
        items.push(...c.items);
        campaignIds.push(c.id);
        used += cost;
    }
    return { items, campaignIds, next: firstSkipped !== null && firstSkipped !== void 0 ? firstSkipped : pos };
}
function campaignSeconds(c) {
    return c.items.reduce((sum, i) => sum + Math.max(0, i.durationSeconds), 0);
}
function revisionOf(content, breaks, playlists, assignment, templates, adSlots = []) {
    const parts = [
        ...content.map((c) => `${c.startsAt}|${c.endsAt}|${c.source}|${c.playlistId}`),
        ...breaks.map((b) => `${b.startsAt}|${b.durationSeconds}|${b.campaignIds.join(",")}`),
        ...playlists.map((p) => `${p.id}|${p.soundEnabled}|${p.items
            .map((i) => {
            var _a, _b, _c, _d;
            return `${i.kind}:${i.assetUrl}:${(_a = i.webUrl) !== null && _a !== void 0 ? _a : ""}:${(_b = i.contentAssetId) !== null && _b !== void 0 ? _b : ""}:${(_c = i.encrypted) !== null && _c !== void 0 ? _c : ""}:${(_d = i.templateInstanceId) !== null && _d !== void 0 ? _d : ""}:${i.durationSeconds}:${i.fit}:${i.muted}`;
        })
            .join(",")}`),
        ...templates.map((t) => `tpl:${t.instanceId}:${t.templateId}:${t.version}:${Object.keys(t.values)
            .sort()
            .map((k) => `${k}=${t.values[k]}`)
            .join("|")}`),
        `sound=${assignment.soundEnabled}`,
        `orientation=${assignment.orientation}`,
        ...adSlots.map((a) => `ad:${a.startsAt}|${a.maxSeconds}`),
    ];
    return fnv1a(parts.join("\n"));
}
export function fnv1a(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
}
export function itemSeconds(item, playlist) {
    return item.durationSeconds > 0
        ? item.durationSeconds
        : playlist.defaultItemSeconds;
}
export function playheadAt(timeline, now) {
    var _a;
    const t = now.getTime();
    const interval = (_a = timeline.content.find((c) => Date.parse(c.startsAt) <= t && t < Date.parse(c.endsAt))) !== null && _a !== void 0 ? _a : timeline.content.find((c) => {
        const start = Date.parse(c.startsAt);
        return t < start && start - t <= CLOCK_SKEW_GRACE_MS;
    });
    if (!interval)
        return null;
    const playlist = timeline.playlists.find((p) => p.id === interval.playlistId);
    if (!playlist)
        return null;
    const items = playlist.items;
    if (items.length === 0) {
        return {
            interval,
            playlist,
            item: null,
            index: 0,
            elapsedSeconds: 0,
            itemSeconds: 0,
            loopSeconds: 0,
        };
    }
    const durations = items.map((i) => itemSeconds(i, playlist));
    const loopSeconds = durations.reduce((a, b) => a + b, 0);
    if (loopSeconds <= 0) {
        return {
            interval,
            playlist,
            item: items[0],
            index: 0,
            elapsedSeconds: 0,
            itemSeconds: 0,
            loopSeconds: 0,
        };
    }
    const anchor = Date.parse(interval.loopStartsAt) || Date.parse(interval.startsAt);
    const since = Math.max(0, t - anchor);
    let offset = Math.floor(since / 1000) % loopSeconds;
    let index = 0;
    while (index < durations.length && offset >= durations[index]) {
        offset -= durations[index];
        index += 1;
    }
    if (index >= items.length)
        index = items.length - 1;
    return {
        interval,
        playlist,
        item: items[index],
        index,
        elapsedSeconds: offset,
        itemSeconds: durations[index],
        loopSeconds,
    };
}
export function breakMayInterrupt(timeline, now) {
    const head = playheadAt(timeline, now);
    if (!head)
        return false;
    if (isChannelPlaylistId(head.playlist.id))
        return false;
    if (!head.item)
        return false;
    if (head.item.live)
        return false;
    return true;
}
export function breakAt(timeline, now) {
    const t = now.getTime();
    const brk = timeline.breaks.find((b) => {
        const start = Date.parse(b.startsAt);
        if (Number.isNaN(start))
            return false;
        return start <= t && t < start + Math.max(0, b.durationSeconds) * 1000;
    });
    if (!brk)
        return null;
    if (!breakMayInterrupt(timeline, now))
        return null;
    let offset = Math.floor((t - Date.parse(brk.startsAt)) / 1000);
    for (let index = 0; index < brk.items.length; index += 1) {
        const item = brk.items[index];
        const seconds = Math.max(0, item.durationSeconds);
        if (seconds === 0)
            continue;
        if (offset < seconds) {
            return { brk, item, index, elapsedSeconds: offset, itemSeconds: seconds };
        }
        offset -= seconds;
    }
    return null;
}
export function nextBreakInSeconds(timeline, now) {
    const t = now.getTime();
    let soonest = null;
    for (const b of timeline.breaks) {
        const start = Date.parse(b.startsAt);
        if (Number.isNaN(start) || start <= t)
            continue;
        if (soonest === null || start < soonest)
            soonest = start;
    }
    return soonest === null ? null : Math.round((soonest - t) / 1000);
}
export function formatClock(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(s / 60);
    return `${m.toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
}
export function formatDuration(totalSeconds) {
    const s = Math.max(0, Math.round(totalSeconds));
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    if (s < 60)
        return `${s} s`;
    if (h === 0)
        return `${m} min`;
    if (m === 0)
        return `${h} h`;
    return `${h} h ${m} min`;
}
export const __testing = {
    subtract,
    rotationOrder,
    rotationRegime,
    fillBreak,
    DAY_KEYS,
};
